import {
  Box,
  getBoundingBox,
  Point,
} from "@/features/Editor/Canvas/canvas-geometry";
import type { ElementDraft } from "@repo/video-engine-i2v/utils/element-draft";
import type { Gesture, Modifiers, PressContext } from "./canvas-gesture";
import {
  getGuidelines,
  getSnapTargets,
  SnapTargets,
  snapLine,
} from "./canvas-snapping";

const MIN_FONT_SIZE = 5;

export type ResizeHandle =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "top"
  | "right"
  | "bottom"
  | "left";

// Pressed on a resize handle. Scales the selected elements relative to the selection box,
// keeping the opposite corner or edge in place.
export function createResizeGesture(
  {
    start,
    canvasElements,
    elements,
    selectedIds,
    resolution,
    scale,
  }: PressContext,
  handle: ResizeHandle,
): Gesture {
  const resizingElements = canvasElements.filter(({ externalId }) =>
    selectedIds.includes(externalId),
  );
  const fontSizes: Record<string, number> = {};

  for (const { externalId } of resizingElements) {
    const element = elements[externalId];

    if (element?.type === "TEXT") {
      fontSizes[externalId] = element.typography.fontSize;
    }
  }
  const selectionBox = getBoundingBox(resizingElements.map(({ box }) => box));
  const snapTargets = getSnapTargets(
    canvasElements
      .filter((element) => !resizingElements.includes(element))
      .map(({ box }) => box),
    resolution,
  );

  return {
    press: () => ({}),
    drag: (point, modifiers) => {
      const resizedBox =
        selectionBox &&
        getResizedBox(
          selectionBox,
          handle,
          start,
          point,
          modifiers,
          snapTargets,
          scale,
        );

      if (!selectionBox || !resizedBox) {
        return {};
      }

      return {
        drafts: Object.fromEntries(
          resizingElements.map(({ externalId, box }) => {
            const fontSize = fontSizes[externalId];

            return [
              externalId,
              fontSize
                ? scaleText(box, fontSize, selectionBox, resizedBox)
                : scaleBox(box, selectionBox, resizedBox),
            ];
          }),
        ),
        guidelines: getGuidelines(resizedBox, snapTargets),
      };
    },
    click: () => ({}),
  };
}

// Corners keep the aspect ratio unless Shift is held; edges stretch one axis.
// The moving edges snap to the targets; with a locked aspect ratio the closer snap
// decides the size. Returns null when the box would collapse below 1px.
function getResizedBox(
  box: Box,
  handle: ResizeHandle,
  start: Point,
  point: Point,
  { shift }: Modifiers,
  snapTargets: SnapTargets,
  scale: number,
): Box | null {
  const isLeft = handle === "left" || handle.endsWith("-left");
  const isTop = handle === "top" || handle.startsWith("top-");
  const resizesWidth = handle !== "top" && handle !== "bottom";
  const resizesHeight = handle !== "left" && handle !== "right";
  const isAspectLocked = !shift && resizesWidth && resizesHeight;

  const dx = point.x - start.x;
  const dy = point.y - start.y;
  let widthScale = resizesWidth ? (box.w + (isLeft ? -dx : dx)) / box.w : 1;
  let heightScale = resizesHeight ? (box.h + (isTop ? -dy : dy)) / box.h : 1;

  if (isAspectLocked) {
    widthScale = heightScale = (widthScale + heightScale) / 2;
  }

  const xSnap = resizesWidth
    ? snapLine(
        isLeft
          ? box.x + box.w - box.w * widthScale
          : box.x + box.w * widthScale,
        snapTargets.x,
        scale,
      )
    : null;
  const ySnap = resizesHeight
    ? snapLine(
        isTop
          ? box.y + box.h - box.h * heightScale
          : box.y + box.h * heightScale,
        snapTargets.y,
        scale,
      )
    : null;
  const snappedWidthScale =
    widthScale + (xSnap ? (isLeft ? -xSnap.offset : xSnap.offset) / box.w : 0);
  const snappedHeightScale =
    heightScale + (ySnap ? (isTop ? -ySnap.offset : ySnap.offset) / box.h : 0);

  if (!isAspectLocked) {
    widthScale = snappedWidthScale;
    heightScale = snappedHeightScale;
  } else if (xSnap && (!ySnap || xSnap.distance <= ySnap.distance)) {
    widthScale = heightScale = snappedWidthScale;
  } else if (ySnap) {
    widthScale = heightScale = snappedHeightScale;
  }

  const w = Math.round(box.w * widthScale);
  const h = Math.round(box.h * heightScale);

  if (w < 1 || h < 1) {
    return null;
  }

  return {
    x: isLeft ? box.x + box.w - w : box.x,
    y: isTop ? box.y + box.h - h : box.y,
    w,
    h,
  };
}

function scaleBox(box: Box, from: Box, to: Box): Box {
  const widthScale = to.w / from.w;
  const heightScale = to.h / from.h;

  return {
    x: Math.round(to.x + (box.x - from.x) * widthScale),
    y: Math.round(to.y + (box.y - from.y) * heightScale),
    w: Math.round(box.w * widthScale),
    h: Math.round(box.h * heightScale),
  };
}

// Text can't stretch, so it scales uniformly by the width factor and only its position
// follows the selection on both axes. The box is derived from the font size, which is
// what the text is measured with after commit.
function scaleText(
  box: Box,
  fontSize: number,
  from: Box,
  to: Box,
): ElementDraft {
  const draftFontSize =
    Math.round(Math.max(fontSize * (to.w / from.w), MIN_FONT_SIZE) * 100) / 100;
  const scale = draftFontSize / fontSize;
  const { x, y } = scaleBox(box, from, to);

  return {
    x,
    y,
    w: box.w * scale,
    h: box.h * scale,
    fontSize: draftFontSize,
  };
}
